/**
 * Done. CPD — Dashboard External CPD agent (Stitch v2 / screen 7)
 *
 * Self-contained inline flow:
 *   idle      → URL input + "Generate from URL" button
 *   reading   → loading state while /api/ai/scrape-url runs
 *   review    → editable draft (title / desc / hours / type / date) +
 *               AI outcome+reflection previews
 *   saving    → POST /api/activities then POST /api/reflections
 *   done      → success state; resets to idle after a few seconds
 *
 * Hours field is highlighted amber when the AI couldn't determine a
 * duration from the page, so the practitioner knows to enter one.
 *
 * Auto-save reflection: when the activity is created we immediately also
 * persist the AI-drafted outcome/reflection against it (aiGenerated=true).
 * The practitioner can edit later via the activities page.
 */

import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { todayIso } from '@/lib/utils';
import type {
  Activity,
  ActivityType,
  CreateActivityInput,
  ScrapeUrlResult,
} from '@/lib/types';

type Phase = 'idle' | 'reading' | 'review' | 'saving' | 'done';

interface ExternalCPDCardProps {
  /** Optional callback so the dashboard can refresh after a save. */
  onActivityCreated?: (activity: Activity) => void;
}

interface Draft {
  title: string;
  description: string;
  hours: string;
  activityTypeId: string;
  activityDate: string;
  sourceUrl: string;
  outcomeText: string;
  reflectionText: string;
  hoursInferred: boolean;
}

const EMPTY_DRAFT: Draft = {
  title: '',
  description: '',
  hours: '',
  activityTypeId: '',
  activityDate: todayIso(),
  sourceUrl: '',
  outcomeText: '',
  reflectionText: '',
  hoursInferred: true,
};

export function ExternalCPDCard({ onActivityCreated }: ExternalCPDCardProps = {}): React.ReactElement {
  const [phase, setPhase] = useState<Phase>('idle');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [activityTypes, setActivityTypes] = useState<ActivityType[] | null>(null);

  // Lazy-load activity types once the user starts engaging with the card
  useEffect(() => {
    if (activityTypes !== null) return;
    if (phase === 'idle') return;
    void (async () => {
      try {
        const types = await api.get<ActivityType[]>('/api/activity-types');
        setActivityTypes(types);
      } catch {
        // Silently ignore — the user can still save; type select will show empty
      }
    })();
  }, [phase, activityTypes]);

  async function handleGenerate(): Promise<void> {
    const trimmed = url.trim();
    if (trimmed.length === 0) return;
    setPhase('reading');
    setError(null);
    try {
      const result = await api.post<ScrapeUrlResult>('/api/ai/scrape-url', { url: trimmed });
      setDraft({
        title: result.suggestedTitle,
        description: result.suggestedDescription,
        hours: result.suggestedHours > 0 ? String(result.suggestedHours) : '',
        activityTypeId: '',
        activityDate: todayIso(),
        sourceUrl: result.sourceUrl,
        outcomeText: result.outcomeText,
        reflectionText: result.reflectionText,
        hoursInferred: result.suggestedHours > 0,
      });
      setPhase('review');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message
        : err instanceof Error ? err.message
        : 'Could not read that URL. Please try another link or log the activity manually.';
      setError(msg);
      setPhase('idle');
    }
  }

  async function handleSave(): Promise<void> {
    if (phase !== 'review') return;
    setError(null);

    if (!draft.title.trim()) { setError('Title is required.'); return; }
    if (!draft.activityTypeId) { setError('Please choose an activity type.'); return; }
    const hoursNum = parseFloat(draft.hours);
    if (isNaN(hoursNum) || hoursNum <= 0) { setError('Please enter how many hours this activity took.'); return; }
    if (hoursNum > 20) { setError('A single activity cannot exceed 20 hours.'); return; }

    setPhase('saving');
    try {
      const input: CreateActivityInput = {
        activityTypeId: draft.activityTypeId,
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        hours: hoursNum,
        activityDate: draft.activityDate,
        sourceUrl: draft.sourceUrl,
      };
      const activity = await api.post<Activity>('/api/activities', input);

      // Best-effort reflection auto-save — never block the activity success on this
      if (draft.outcomeText.trim() && draft.reflectionText.trim()) {
        try {
          await api.post('/api/reflections', {
            activityId: activity.id,
            outcomeText: draft.outcomeText.trim(),
            reflectionText: draft.reflectionText.trim(),
            aiGenerated: true,
          });
        } catch (reflectErr) {
          console.warn('Reflection auto-save failed; activity still created.', reflectErr);
        }
      }

      onActivityCreated?.(activity);
      setPhase('done');
      setUrl('');
      setTimeout(() => {
        setDraft(EMPTY_DRAFT);
        setPhase('idle');
      }, 2500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save activity. Please try again.';
      setError(msg);
      setPhase('review');
    }
  }

  function handleCancel(): void {
    setDraft(EMPTY_DRAFT);
    setUrl('');
    setError(null);
    setPhase('idle');
  }

  function updateDraft<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft(prev => ({ ...prev, [key]: value }));
  }

  return (
    <div className="bg-[var(--stitch-primary)] p-8 md:p-10 rounded-xl stitch-editorial-shadow text-[var(--stitch-on-primary)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="stitch-font-label text-[12px] text-[var(--stitch-primary-fixed)] tracking-[0.2em] uppercase">
          Log External CPD
        </h3>
        <span className="stitch-font-label text-[10px] text-[var(--stitch-primary-fixed)]/70 tracking-widest uppercase">
          AI-assisted
        </span>
      </div>

      {/* ── idle: URL input ───────────────────────────────────────── */}
      {phase === 'idle' && (
        <>
          <p className="stitch-font-body text-[15px] text-[var(--stitch-primary-fixed-dim)]/90 mb-6 leading-relaxed">
            Paste a conference, webinar, or podcast link. We&apos;ll read the page and draft a CPD
            entry — title, summary, hours, and a Board-compliant reflection — ready for you to review.
          </p>
          <div className="flex flex-col gap-3">
            <input
              type="url"
              value={url}
              onChange={e => { setUrl(e.target.value); }}
              placeholder="https://example.com/conference"
              className="w-full bg-[var(--stitch-primary-container)]/50 border border-[var(--stitch-on-primary-container)]/30 rounded-lg px-5 py-4 text-white text-[15px] placeholder:text-[var(--stitch-primary-fixed-dim)]/60 focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none transition-all"
              aria-label="Activity URL"
            />
            <button
              type="button"
              onClick={() => { void handleGenerate(); }}
              disabled={url.trim().length === 0}
              className="bg-[var(--stitch-primary-fixed)] text-[var(--stitch-on-primary-fixed)] font-bold py-4 rounded-lg stitch-font-label text-[14px] uppercase tracking-wider hover:bg-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Generate from URL
            </button>
            <p className="stitch-font-label text-[11px] text-[var(--stitch-primary-fixed-dim)]/70 text-center uppercase tracking-widest">
              Or scroll down to log manually
            </p>
          </div>
          {error && (
            <p role="alert" className="mt-4 rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-[13px] text-white">
              {error}
            </p>
          )}
        </>
      )}

      {/* ── reading: loading state ────────────────────────────────── */}
      {phase === 'reading' && (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary-fixed)] text-5xl">progress_activity</span>
          <p className="stitch-font-body text-[15px] text-[var(--stitch-primary-fixed)]">
            Reading the page and drafting your activity…
          </p>
          <p className="stitch-font-body text-[12px] text-[var(--stitch-primary-fixed-dim)]/70 italic">
            Usually takes 10–15 seconds.
          </p>
        </div>
      )}

      {/* ── review: editable draft ────────────────────────────────── */}
      {(phase === 'review' || phase === 'saving') && (
        <div className="flex flex-col gap-4">
          <p className="stitch-font-body text-[13px] text-[var(--stitch-primary-fixed)]/90 italic">
            Review the AI-generated draft below, fill in anything missing, then save.
          </p>

          <FieldShell label="Title" required>
            <input
              type="text"
              value={draft.title}
              onChange={e => { updateDraft('title', e.target.value); }}
              className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-4 py-3 text-[15px] focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
            />
          </FieldShell>

          <FieldShell label="Description">
            <textarea
              value={draft.description}
              onChange={e => { updateDraft('description', e.target.value); }}
              rows={3}
              className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-4 py-3 text-[14px] resize-y focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
            />
          </FieldShell>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldShell
              label="Hours"
              required
              warning={!draft.hoursInferred ? 'We couldn’t find a duration on the page — please enter it.' : null}
            >
              <input
                type="number"
                min={0.5}
                max={20}
                step={0.5}
                value={draft.hours}
                onChange={e => { updateDraft('hours', e.target.value); }}
                placeholder="e.g. 4"
                className={`w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-4 py-3 text-[15px] outline-none focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] ${
                  !draft.hoursInferred
                    ? 'ring-2 ring-amber-400/80'
                    : ''
                }`}
              />
            </FieldShell>

            <FieldShell label="Activity date" required>
              <input
                type="date"
                value={draft.activityDate}
                max={todayIso()}
                onChange={e => { updateDraft('activityDate', e.target.value); }}
                className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-4 py-3 text-[15px] focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
              />
            </FieldShell>
          </div>

          <FieldShell label="Activity type" required>
            <select
              value={draft.activityTypeId}
              onChange={e => { updateDraft('activityTypeId', e.target.value); }}
              className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-4 py-3 text-[15px] focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
            >
              <option value="">{activityTypes ? 'Select activity type…' : 'Loading types…'}</option>
              {activityTypes?.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </FieldShell>

          {/* Outcome + reflection previews (editable) */}
          <details className="bg-white/10 rounded-lg p-4 mt-2" open>
            <summary className="cursor-pointer stitch-font-label text-[12px] uppercase tracking-wider text-[var(--stitch-primary-fixed)]">
              AI-drafted Outcome &amp; Reflection (auto-saved on save)
            </summary>
            <div className="mt-3 space-y-3">
              <FieldShell label="Outcome (what you learned)" subtle>
                <textarea
                  value={draft.outcomeText}
                  onChange={e => { updateDraft('outcomeText', e.target.value); }}
                  rows={3}
                  className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-3 py-2 text-[13px] resize-y focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
                />
              </FieldShell>
              <FieldShell label="Reflection (impact on practice)" subtle>
                <textarea
                  value={draft.reflectionText}
                  onChange={e => { updateDraft('reflectionText', e.target.value); }}
                  rows={3}
                  className="w-full bg-white/95 text-[var(--stitch-on-surface)] rounded-lg px-3 py-2 text-[13px] resize-y focus:ring-2 focus:ring-[var(--stitch-primary-fixed)] outline-none"
                />
              </FieldShell>
            </div>
          </details>

          <p className="stitch-font-label text-[10px] text-[var(--stitch-primary-fixed-dim)]/70 uppercase tracking-widest break-all">
            Source: {draft.sourceUrl}
          </p>

          {error && (
            <p role="alert" className="rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-[13px] text-white">
              {error}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={phase === 'saving'}
              className="px-5 py-3 stitch-font-label text-[13px] uppercase tracking-wider text-[var(--stitch-primary-fixed)] hover:text-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { void handleSave(); }}
              disabled={phase === 'saving'}
              className="px-8 py-3 stitch-font-label text-[14px] uppercase tracking-wider bg-[var(--stitch-primary-fixed)] text-[var(--stitch-on-primary-fixed)] font-bold rounded-full hover:bg-white transition-colors disabled:opacity-60"
            >
              {phase === 'saving' ? 'Saving…' : 'Save Activity'}
            </button>
          </div>
        </div>
      )}

      {/* ── done: success ─────────────────────────────────────────── */}
      {phase === 'done' && (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <span className="material-symbols-outlined is-filled text-[var(--stitch-primary-fixed)] text-5xl">check_circle</span>
          <p className="stitch-font-headline text-[18px] text-[var(--stitch-primary-fixed)] uppercase">
            Activity logged.
          </p>
          <p className="stitch-font-body text-[13px] text-[var(--stitch-primary-fixed-dim)]/80">
            Your reflection draft was saved too — edit anytime from the activities page.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function FieldShell({
  label,
  required = false,
  subtle = false,
  warning = null,
  children,
}: {
  label: string;
  required?: boolean;
  subtle?: boolean;
  warning?: string | null;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <label className={`stitch-font-label tracking-wider uppercase ${subtle ? 'text-[11px] text-[var(--stitch-primary-fixed)]/80' : 'text-[12px] text-[var(--stitch-primary-fixed)]'}`}>
        {label}
        {required && <span className="ml-1 text-amber-300" aria-hidden="true">*</span>}
      </label>
      {children}
      {warning && (
        <p className="text-[12px] text-amber-200 stitch-font-body italic">
          {warning}
        </p>
      )}
    </div>
  );
}
